import React from 'react';
import { Table, Text, EmptyState } from '@clockout-dev/design-elements';
import { formatFullCurrency, formatNumberWithCommas } from '../../../utils/numbers';
import StatementStatusPill from './StatementStatusPill';
import styles from './styles/billing-statements-card.module.scss';

/**
 * Statements list (Figma 6085:9006 — "Transaction History" card).
 *
 * Mirrors the team-page table chrome (`SectionCard` + design-elements
 * `Table` with `TableVariants.HISTORY`) so billing matches the rest of
 * the admin visually. Table padding/typography overrides
 * (`tableShell` / `tableRoot`) are cloned from `team.module.scss` and
 * `transaction-history-card.module.scss`.
 *
 * Selection: maps the controller's `selectedStatements` Set onto the
 * `Table.rowSelection` API. Selecting / unselecting individual rows or
 * the header checkbox (which supports the indeterminate state out of
 * the box) calls `onSelectionChange` with the updated Set so the parent
 * controller's `exportToXLSX()` keeps working without changes.
 *
 * @param {Object} props
 * @param {Array<Object>} props.statements Statements payload from the API.
 * @param {boolean} props.loading Whether the table should show its spinner.
 * @param {Set<string>} props.selectedStatementIds Selected statement ids.
 * @param {(nextSelection: Set<string>) => void} props.onSelectionChange
 */
function BillingStatementsCard({
    statements = [],
    loading,
    selectedStatementIds,
    onSelectionChange,
}) {
    const columns = [
        {
            title: 'Date',
            dataIndex: 'dateRange',
            key: 'dateRange',
            render: (value) => (
                <Text as="span" size="s" className={styles.dateCell}>
                    {value}
                </Text>
            ),
        },
        {
            title: 'Billable Users',
            dataIndex: 'enrolledUsers',
            key: 'enrolledUsers',
            render: (value) => (
                <Text as="span" size="s" className={styles.numericCell}>
                    {formatNumberWithCommas(value ?? 0)}
                </Text>
            ),
        },
        {
            title: 'Invoice Amount',
            dataIndex: 'invoiceAmount',
            key: 'invoiceAmount',
            render: (value) => (
                <Text as="span" size="s" className={styles.amountCell}>
                    {formatFullCurrency(value)}
                </Text>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            align: 'right',
            render: (status) => <StatementStatusPill status={status} />,
        },
    ];

    const selectedRowKeys = selectedStatementIds ? Array.from(selectedStatementIds) : [];

    const rowSelection = {
        type: 'checkbox',
        selectedRowKeys,
        onChange: (nextKeys) => onSelectionChange?.(new Set(nextKeys)),
    };

    return (
        <Table
            columns={columns}
            dataSource={statements}
            loading={loading}
            pagination={false}
            rowKey={(statement) => statement.id}
            rowSelection={rowSelection}
            emptyState={<EmptyState title="No statements yet." />}
            classNames={{ shell: styles.tableShell, root: styles.tableRoot }}
        />
    );
}

export default BillingStatementsCard;
